import {
  CollapsibleCard,
  IconButton,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, BasicTable } from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import { closeModal, openModal } from '@store/modals';
import { InformationIcon } from '@atgfr8/icons';
import { PaymentViewModel } from '@typeDefinitions/paymentTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';
import { formatAmount } from '@utils/utils';
import { get, size } from 'lodash';
import { createSignal, Show } from 'solid-js';

import { checkDetailsModalId } from '../CarrierPayments';
import { CheckDetails } from '../CheckDetails';
import classes from './classes';

type Props = {
  index: number;
  vendor: VendorContractViewModel;
};

const iconStyles = {
  color: '#0070A2',
  cursor: 'pointer',
};

export const VendorPayments = (props: Props) => {
  const getFilteredLineItems = () =>
    get(props.vendor, 'lineItems', []).filter(
      (item) => item.operationType !== 'Delete',
    );
  const [activeRow, setActiveRow] = createSignal<PaymentViewModel | null>(null);

  const calculateTotalLineItems = () => {
    return size(getFilteredLineItems()) > 0
      ? getFilteredLineItems().reduce(
          (acc, item) => acc + Number(item.rate) * Number(item.quantity),
          0,
        )
      : 0;
  };

  const calculateTotalPayments = () => {
    return size(get(props.vendor, 'payments')) > 0
      ? get(props.vendor, 'payments', [])
          .filter((item) => item.operationType !== 'Delete')
          .reduce((acc, item) => acc + Number(item.amount), 0)
      : 0;
  };

  const columns: Column<PaymentViewModel>[] = [
    {
      name: 'Date',
      key: 'Date',
      renderCell: (rowData) => rowData.paidDate,
    },
    {
      name: 'Description',
      key: 'description',
      minWidth: 200,
      renderCell: (rowData) => rowData.description,
    },
    {
      name: 'Reference',
      key: 'reference',
      renderCell: (rowData) => rowData.referenceNum,
    },
    {
      name: 'Amount',
      key: 'amount',
      renderCell: (rowData) => <>{Number(rowData.amount).toFixed(2)}</>,
    },
    {
      name: 'Payment #',
      key: 'payment',
      renderCell: (rowData) => <>{rowData.checkNumber}</>,
    },
    {
      name: 'Info',
      key: 'info',
      renderCell: (rowData) => (
        <Show when={rowData.checkNumber} fallback={<div class="w-10"></div>}>
          <IconButton
            onClick={() => {
              setActiveRow(rowData);
              openModal(checkDetailsModalId);
            }}
            dataTestId={`view-vendor-payment-info-button-${rowData.id}`}
          >
            <InformationIcon style={{ ...iconStyles, 'font-size': '1.5rem' }} />
          </IconButton>
        </Show>
      ),
    },
  ];

  const footerRow = [
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalLabel}>
          Outstanding
        </Typography>
      ),
      key: 'Outstanding',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalAmount}>
          {formatAmount(calculateTotalLineItems() - calculateTotalPayments())}
        </Typography>
      ),
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalLabel}>
          Total Paid
        </Typography>
      ),
      key: '#',
    },
    {
      key: '#',
      renderCell: () => (
        <Typography variant="body1" class={classes.totalAmount}>
          {formatAmount(calculateTotalPayments())}
        </Typography>
      ),
    },
  ];

  return (
    <CollapsibleCard startTitle="Vendor Payments" class="mb-2">
      <BasicTable<PaymentViewModel>
        footerRow={footerRow}
        footerBorder={false}
        tableBodyClasses={classes.tableBg}
        tableFooterClasses={classes.tableFooter}
        tableBodyRowClasses={classes.tableFooterRow}
        columns={columns}
        rows={props.vendor.payments || []}
        tableFooterRowClasses={classes.tableFooterRow}
      />
      <BasicModal
        title="Check Details"
        id={checkDetailsModalId}
        onClose={() => closeModal(checkDetailsModalId)}
        showClose={true}
        header={false}
        width={'40vw'}
        footer={false}
      >
        <CheckDetails activeRow={activeRow()!} />
      </BasicModal>
    </CollapsibleCard>
  );
};
